import { BellOff, BellRing, TriangleAlert } from 'lucide-react';
import { useGetReminderPreviewQuery } from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

export const OFFSET_LABELS: Record<number, string> = {
  168: 'A week before',
  72: 'Three days before',
  48: 'Two days before',
  24: 'The day before',
  2: 'Two hours before',
};

/*
  What the reminder job would do, before it does it.

  ── Why this screen exists ─────────────────────────────────────────────────

  Every other message SafePass sends is a direct consequence of something a
  person just did. Reminders are the only ones a cron sends on its own, to many
  people at once, with nobody watching — so the alternative way to find out who
  one reaches is to send it and see. The figures here come from the same
  audience query and the same per-phone deduplication the job uses, so the
  number on screen is the number that would be billed.

  ── Why the three gates are listed separately ──────────────────────────────

  "Reminders are not going out" has three possible causes and a single combined
  warning would make you guess which. Each is stated on its own line with what
  to do about it.
*/
export function RemindersDialog({
  eventId,
  institutionId,
  onClose,
}: {
  eventId: string | null;
  institutionId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetReminderPreviewQuery(
    { institutionId, eventId: eventId ?? '' },
    { skip: !eventId },
  );

  const blocked = data && (!data.sendingEnabled || !data.isPublished || data.offsets.length === 0);

  return (
    <Modal
      open={eventId !== null}
      onClose={onClose}
      size="lg"
      title="Reminders"
      description={data?.title}
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {isLoading || !data ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {/* ── Why nothing is going out, if nothing is ──────────────────── */}
          {blocked && (
            <div className={cn(styles.panel, 'flex flex-col gap-1.5 p-3')}>
              <p className="flex items-center gap-1.5 text-[12px] font-bold text-foreground">
                <BellOff size={13} /> No reminders will be sent for this event
              </p>
              <ul className="flex flex-col gap-1">
                {!data.sendingEnabled && (
                  <li className={styles.meta}>
                    · Reminder sending is <strong>switched off for the whole server</strong>. Set{' '}
                    <code className="rounded bg-muted px-1">EVENT_REMINDERS_ENABLED=true</code> in the API
                    environment and restart.
                  </li>
                )}
                {!data.isPublished && <li className={styles.meta}>· This event is not published yet.</li>}
                {data.offsets.length === 0 && (
                  <li className={styles.meta}>· No reminder times are ticked. Edit the event to add one.</li>
                )}
              </ul>
            </div>
          )}

          {/* ── Who it reaches ───────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-2">
            <div className={cn(styles.panel, 'px-3 py-2')}>
              <p className={styles.label}>Messages per reminder</p>
              <p className={cn(styles.figure, 'mt-0.5 text-[18px]')}>{data.recipientCount}</p>
              <p className={styles.meta}>
                one per phone, covering {data.childCount} child{data.childCount === 1 ? '' : 'ren'}
              </p>
            </div>
            <div className={cn(styles.panel, 'px-3 py-2')}>
              <p className={styles.label}>Total if all send</p>
              <p className={cn(styles.figure, 'mt-0.5 text-[18px]')}>
                {data.recipientCount * data.offsets.length}
              </p>
              <p className={styles.meta}>
                {data.offsets.length} reminder time{data.offsets.length === 1 ? '' : 's'} × {data.recipientCount}
              </p>
            </div>
          </div>

          {/* ── The schedule, with the exact text ────────────────────────── */}
          {data.schedule.length > 0 && (
            <div>
              <p className={cn(styles.label, 'mb-1.5')}>Schedule</p>
              <div className={cn(styles.panel, 'divide-y divide-border')}>
                {data.schedule.map((s) => (
                  <div key={s.offsetHours} className="px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11.5px] font-semibold text-foreground">
                        {OFFSET_LABELS[s.offsetHours] ?? `${s.offsetHours}h before`}
                      </p>
                      {s.state === 'done' ? (
                        <Mark tone={s.failedCount > 0 ? 'danger' : 'success'}>
                          {s.sentCount} sent{s.failedCount > 0 && `, ${s.failedCount} failed`}
                        </Mark>
                      ) : s.state === 'due' ? (
                        <Mark tone="warning">Due now</Mark>
                      ) : s.state === 'missed' ? (
                        <Mark tone="neutral">Window passed</Mark>
                      ) : (
                        <Mark tone="info">Scheduled</Mark>
                      )}
                    </div>
                    <p className={cn(styles.meta, 'mt-0.5')}>{stampFmt.format(new Date(s.triggerAt))}</p>
                    {/* The exact SMS, so nobody has to guess what lands. */}
                    <p className="mt-1.5 rounded-md border border-border bg-muted px-2 py-1.5 text-[11px] leading-relaxed text-foreground">
                      {s.sampleMessage}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.schedule.some((s) => s.state === 'missed') && (
            <p className={cn(styles.meta, 'flex items-start gap-1.5')}>
              <TriangleAlert size={12} className="mt-0.5 flex-shrink-0" />
              A passed window is not retried. Reminders only fire within six hours of their time, so
              switching sending on later cannot blast a term of back-dated messages.
            </p>
          )}

          {/* ── Exactly which numbers ────────────────────────────────────── */}
          {data.recipients.length > 0 && (
            <details>
              <summary className="cursor-pointer text-[11.5px] font-semibold text-foreground">
                <BellRing size={12} className="mr-1 inline" />
                The {data.recipients.length} number{data.recipients.length === 1 ? '' : 's'} this reaches
              </summary>
              <div className={cn(styles.panel, 'mt-2 divide-y divide-border')}>
                {data.recipients.map((r) => (
                  <div key={r.phone} className="flex items-center justify-between px-3 py-1.5">
                    <span className="text-[11.5px] tabular-nums text-foreground">{r.phone}</span>
                    <span className={styles.meta}>
                      {r.childCount} child{r.childCount === 1 ? '' : 'ren'}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Modal>
  );
}
